import { notifications } from '@mantine/notifications';
import { IconBell } from '@tabler/icons-react';
import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { io, type ManagerOptions, type Socket, type SocketOptions } from 'socket.io-client';
import { REALTIME_PATH, type NotificationDto, type RealtimeClientEvents, type RealtimeServerEvents } from '@mexp/shared';
import { API_BASE, getAccessToken, http, onAccessTokenChange } from '@/api/client';
import { useAuth } from '@/auth/AuthProvider';
import { portalKeys } from '@/features/portal/api';
import { drKeys } from '@/features/requests/api';
import { notificationKeys } from '@/layout/NotificationBell';

type LiveSocket = Socket<RealtimeServerEvents, RealtimeClientEvents>;

/** A burst of changes (one action often touches several tasks) causes one refetch. */
const REFRESH_DELAY_MS = 250;

/**
 * Keeps a socket to the API open while someone is signed in, so RA and the departments see each other's work
 * without reloading: task and request changes refresh the open lists, and new notifications pop up and update the bell.
 */
export function RealtimeBridge() {
  const { status, user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const live = status === 'authenticated' && !!user && !user.mustChangePassword;

  useEffect(() => {
    if (!live) return;
    const options: Partial<ManagerOptions & SocketOptions> = {
      path: REALTIME_PATH,
      withCredentials: true,
      // Read at every (re)connect, so a renewed token is always the one presented.
      auth: (cb) => cb({ token: getAccessToken() ?? '' }),
    };
    const socket: LiveSocket = API_BASE ? io(API_BASE, options) : io(options);

    const pending = new Map<string, QueryKey>();
    let timer: number | undefined;
    const refresh = (...keys: QueryKey[]) => {
      keys.forEach((key) => pending.set(JSON.stringify(key), key));
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        pending.forEach((queryKey) => void queryClient.invalidateQueries({ queryKey }));
        pending.clear();
      }, REFRESH_DELAY_MS);
    };
    const refreshAll = () => refresh(drKeys.all, portalKeys.all, notificationKeys.all);

    const show = (n: NotificationDto) =>
      notifications.show({
        id: `live-${n.id}`,
        color: 'brand',
        icon: <IconBell size={18} />,
        title: n.title,
        message: n.body ?? '',
        autoClose: 6000,
        style: n.link ? { cursor: 'pointer' } : undefined,
        onClick: n.link
          ? () => {
              notifications.hide(`live-${n.id}`);
              void http.post(`/notifications/${n.id}/read`).then(() => queryClient.invalidateQueries({ queryKey: notificationKeys.all }));
              navigate(n.link as string);
            }
          : undefined,
      });

    let connectedBefore = false;
    socket.on('connect', () => {
      // Anything pushed while the socket was down was missed: catch up once.
      if (connectedBefore) refreshAll();
      connectedBefore = true;
    });
    // The server closes the socket when the access token it was opened with expires; reconnect with the current one.
    socket.on('disconnect', (reason) => {
      if (reason === 'io server disconnect') socket.connect();
    });
    socket.on('notification:new', (n) => {
      show(n);
      refreshAll();
    });
    socket.on('dr:changed', () => refresh(drKeys.all));

    // A renewed session gets a fresh socket, so the connection never outlives the token it was opened with.
    const unsubscribe = onAccessTokenChange((token) => {
      if (token) socket.disconnect().connect();
    });

    return () => {
      unsubscribe();
      window.clearTimeout(timer);
      socket.close();
    };
  }, [live, queryClient, navigate]);

  return null;
}
